import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../Datas/siteConfig";
import "../css/delivery.scss";
import "../css/returns.scss";

const returnHighlights = [
  {
    label: "Request window",
    value: siteConfig.returnsWindowDays + " days from delivery",
  },
  { label: "Condition", value: "Unworn, tagged & original packaging" },
  { label: "Refund timing", value: "Up to 10 business days after approval" },
];

const returnSteps = [
  {
    title: "Tell us",
    text:
      "Email " +
      siteConfig.contact.customerServiceEmail +
      " with your order number, the item and the reason for the return.",
  },
  {
    title: "Wait for approval",
    text: "We will reply with the return reference, destination and packing instructions. Please do not send anything before receiving these details.",
  },
  {
    title: "Pack with care",
    text: "Place the clean, unworn piece in its original protective packaging and include the return reference inside the parcel.",
  },
  {
    title: "Send & keep proof",
    text: "Use a tracked service and retain the receipt until your refund is complete. Return postage is normally paid by the customer.",
  },
];

const returnSections = [
  {
    id: "eligibility",
    title: "Return eligibility",
    body: [
      "A return request must reach us within " +
        siteConfig.returnsWindowDays +
        " calendar days of delivery. Items must be unused, unworn, unwashed and in resalable condition with all tags, hygiene seals and original packaging intact.",
      "Trying an item on carefully is welcome. We may reduce or decline a refund when a piece shows wear, scent, make-up, damage or handling beyond what is reasonably needed to assess it.",
    ],
  },
  {
    id: "process",
    title: "How to make a return",
    body: [
      "Follow these four steps so we can identify and process your parcel without delay.",
    ],
    showSteps: true,
  },
  {
    id: "exceptions",
    title: "Items we cannot accept",
    body: [
      "For hygiene and made-for-you reasons, some purchases are final unless they arrive faulty. These include personalised or altered pieces, gift cards, and swimwear or intimate items with a removed hygiene seal.",
      "Items marked final sale are not eligible for a change-of-mind return where local consumer law permits. Your statutory rights for faulty or misdescribed goods are unaffected.",
    ],
  },
  {
    id: "refunds",
    title: "Refunds",
    body: [
      "We inspect returns after arrival and email you once they are accepted. The refund is issued to the original payment method; initial expedited-delivery charges, duties and return postage are not normally refundable.",
      "Allow up to 10 business days for inspection and a further period for your bank or card provider to post the credit. Processing times outside our studio are beyond our control.",
    ],
  },
  {
    id: "exchanges",
    title: "Exchanges",
    body: [
      "Stock is produced in limited quantities, so we cannot reserve an alternative while a return travels. For the quickest exchange, request a return and place a new order for the preferred colour or size.",
      "If you would like fit advice before reordering, our customer care team will be happy to help.",
    ],
    contact: true,
  },
  {
    id: "problems",
    title: "Faulty, damaged or incorrect items",
    body: [
      "Please contact us within 48 hours of delivery with your order number and clear photographs of the item and packaging. Keep all packaging until we confirm the next step.",
      "When an item is confirmed as faulty or sent in error, we arrange an appropriate replacement, repair or refund and cover reasonable return costs.",
    ],
    contact: true,
  },
];

function Returns() {
  return (
    <main className="returns policy-page" id="main-content">
      <header className="policy-page__header">
        <p className="policy-page__eyebrow">Customer care</p>
        <h1>Returns</h1>
        <p>
          A clear, considered way to send something back. Please review the
          conditions before returning your piece.
        </p>
      </header>

      <dl className="policy-page__summary" aria-label="Returns at a glance">
        {returnHighlights.map((item) => (
          <div key={item.label}>
            <dt>{item.label}</dt>
            <dd>{item.value}</dd>
          </div>
        ))}
      </dl>

      <div className="policy-page__layout">
        <aside className="policy-page__aside" aria-label="On this page">
          <p>On this page</p>
          <ol>
            {returnSections.map((section) => (
              <li key={section.id}>
                <a href={"#" + section.id}>{section.title}</a>
              </li>
            ))}
          </ol>
          <div className="policy-page__help">
            <span>Unsure about your item?</span>
            <Link to="/contact">Ask customer care</Link>
          </div>
        </aside>

        <article className="policy-page__content">
          {returnSections.map((section, index) => (
            <section id={section.id} key={section.id}>
              <span className="policy-page__number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div>
                <h2>{section.title}</h2>
                {section.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}

                {section.showSteps && (
                  <ol className="returns__steps">
                    {returnSteps.map((step, stepIndex) => (
                      <li key={step.title}>
                        <span aria-hidden="true">{stepIndex + 1}</span>
                        <div>
                          <h3>{step.title}</h3>
                          <p>{step.text}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}

                {section.contact && (
                  <p>
                    Contact{" "}
                    <a
                      href={
                        "mailto:" + siteConfig.contact.customerServiceEmail
                      }
                    >
                      {siteConfig.contact.customerServiceEmail}
                    </a>{" "}
                    and include your order number.
                  </p>
                )}
              </div>
            </section>
          ))}
        </article>
      </div>
    </main>
  );
}

export default Returns;
